import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  BarChart3,
  CreditCard,
  Download,
  ExternalLink,
  FileText,
  HardDrive,
  Loader2,
  RefreshCw,
  Users,
  Wallet,
} from "lucide-react";
import * as React from "react";

import {
  type BillingMode,
  createAdminOrgCheckout,
  createAdminOrgPortal,
  getAdminOrgCreditBalance,
  getAdminOrgPlatformInvoices,
  grantAdminOrgCredit,
  setAdminOrgBillingMode,
} from "@ando/core";
import { Badge, Button, cn, useToast } from "@ando/ui";

import {
  useAdminOrg,
  useAdminOrgInvoices,
  useAdminOrgUsage,
} from "@/features/admin/hooks/useAdminOrg";

import { GlobalMarkupCard } from "../GlobalMarkupCard";
import { ModelMarkupCard } from "../ModelMarkupCard";
import { OrgBillingConfigCard } from "./OrgBillingConfigCard";
import { LoadingRow, PaginationControls, safeNum, usePagination } from "./_shared";

/**
 * Billing tab — org-scoped billing dashboard for platform admins.
 *
 * v1 reference: ``OrgBillingTab.tsx`` (281 LoC). v2 ports the usage
 * meters + per-member breakdown + invoice list, plus the three top-row
 * CTAs from v1's ``OrganizationDetailPage`` (Aggiorna / Gestisci
 * abbonamento / Attiva abbonamento). The Stripe portal + checkout
 * endpoints are currently caller-scoped on the BE
 * (``/billing/org/portal`` + ``/billing/org/checkout``) — see the
 * comment on ``createAdminOrgPortal`` in ``packages/core/.../adminOrgs.ts``.
 */

export interface OrgBillingTabProps {
  orgId: string;
}

function formatBytes(bytes: number | null | undefined): string {
  const safe = safeNum(bytes, 0);
  if (safe === 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let v = safe;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(v >= 100 || i === 0 ? 0 : 1)} ${units[i]}`;
}

function formatEur(amount: number | null | undefined): string {
  const safe = safeNum(amount, 0);
  return new Intl.NumberFormat("it-IT", {
    style: "currency",
    currency: "EUR",
  }).format(safe);
}

function ProgressBar({
  value,
  ariaLabel,
}: {
  value: number | null | undefined;
  ariaLabel: string;
}): React.ReactElement {
  const pct = Math.max(0, Math.min(100, safeNum(value, 0)));
  const high = pct > 80;
  return (
    <div
      role="progressbar"
      aria-label={ariaLabel}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(pct)}
      className="h-2.5 w-full overflow-hidden rounded-full bg-cream-deep"
    >
      <div
        className={cn("h-full rounded-full transition-all", high ? "bg-destructive" : "bg-accent")}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

function formatMinor(amount: number | null, currency: string | null): string {
  const safe = safeNum(amount, 0) / 100;
  try {
    return new Intl.NumberFormat("it-IT", {
      style: "currency",
      currency: (currency ?? "usd").toUpperCase(),
    }).format(safe);
  } catch {
    return `${safe.toFixed(2)} ${(currency ?? "").toUpperCase()}`;
  }
}

/** Platform-usage invoices for this org (marked-up LLM cost), issued by
 * ``platform_invoice_service`` — distinct from the consumer-subscription list. */
function PlatformInvoicesSection({
  orgId,
}: {
  orgId: string;
}): React.ReactElement {
  const q = useQuery({
    queryKey: ["admin", "org", "platform-invoices", orgId],
    queryFn: () => getAdminOrgPlatformInvoices(orgId),
    staleTime: 30_000,
  });
  const invoices = q.data ?? [];
  return (
    <section aria-labelledby="org-platform-invoices" className="flex flex-col gap-2">
      <h3 id="org-platform-invoices" className="font-medium text-[13px] text-ink-1">
        <Trans>Fatture piattaforma</Trans>
      </h3>
      {q.isLoading ? (
        <div className="h-12 animate-pulse rounded-lg bg-ink-1/5" aria-hidden />
      ) : invoices.length === 0 ? (
        <p className="text-[12px] text-ink-3">
          <Trans>Nessuna fattura di consumo per questa organizzazione.</Trans>
        </p>
      ) : (
        <ul className="flex flex-col divide-y divide-hairline rounded-lg border border-hairline">
          {invoices.map((inv) => (
            <li key={inv.id} className="flex items-center justify-between gap-3 px-3 py-2">
              <span className="flex items-center gap-2">
                <FileText size={14} className="text-ink-3" aria-hidden />
                <span className="font-mono text-[12px] text-ink-2">
                  {inv.period ?? inv.id.slice(0, 12)}
                </span>
                {inv.status ? <Badge variant="secondary">{inv.status}</Badge> : null}
              </span>
              <span className="flex items-center gap-3">
                <span className="text-[12.5px] text-ink-1 tabular-nums">
                  {formatMinor(inv.amount_due, inv.currency)}
                </span>
                {inv.hosted_invoice_url ? (
                  <a
                    href={inv.hosted_invoice_url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-accent"
                    aria-label="Apri fattura"
                  >
                    <ExternalLink size={13} aria-hidden />
                  </a>
                ) : null}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** Prepaid plan control: mode toggle (postpaid/prepaid), derived balance,
 * and admin credit grants. The self-serve top-up (customer buys) is separate. */
function CreditsSection({ orgId }: { orgId: string }): React.ReactElement {
  const { i18n } = useLingui();
  const { toast } = useToast();
  const qc = useQueryClient();
  const QK = ["admin", "org", "credit-balance", orgId] as const;
  const q = useQuery({
    queryKey: QK,
    queryFn: () => getAdminOrgCreditBalance(orgId),
    staleTime: 30_000,
  });
  const bal = q.data;
  const [amount, setAmount] = React.useState("");

  const invalidate = () => qc.invalidateQueries({ queryKey: QK });

  const setMode = useMutation({
    mutationFn: (mode: BillingMode) => setAdminOrgBillingMode(orgId, mode),
    onSuccess: () => {
      invalidate();
      toast.success(i18n._(msg`Modello di fatturazione aggiornato`));
    },
    onError: () => toast.error(i18n._(msg`Aggiornamento fallito`)),
  });

  const grant = useMutation({
    mutationFn: () => grantAdminOrgCredit(orgId, Number(amount)),
    onSuccess: () => {
      setAmount("");
      invalidate();
      toast.success(i18n._(msg`Credito assegnato`));
    },
    onError: () => toast.error(i18n._(msg`Assegnazione credito fallita`)),
  });

  const prepaid = bal?.is_prepaid ?? false;
  const amt = Number(amount);
  const canGrant = Number.isFinite(amt) && amt > 0 && !grant.isPending;

  return (
    <section aria-labelledby="org-credits" className="flex flex-col gap-3">
      <div className="flex flex-col gap-0.5">
        <h3 id="org-credits" className="font-medium text-[13px] text-ink-1">
          <Trans>Piano & crediti</Trans>
        </h3>
        <p className="text-[11.5px] text-ink-3">
          <Trans>
            Postpagato = fattura mensile. Prepagato = consuma crediti, bloccato a saldo zero.
          </Trans>
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button
          variant={prepaid ? "outline" : "default"}
          size="sm"
          onClick={() => setMode.mutate("postpaid")}
          disabled={setMode.isPending}
        >
          <Trans>Postpagato</Trans>
        </Button>
        <Button
          variant={prepaid ? "default" : "outline"}
          size="sm"
          onClick={() => setMode.mutate("prepaid")}
          disabled={setMode.isPending}
        >
          <Trans>Prepagato</Trans>
        </Button>
      </div>

      {prepaid && bal ? (
        <div className="flex flex-wrap gap-4 rounded-lg border border-hairline bg-card/55 p-3 text-[12.5px]">
          <span className="flex flex-col">
            <span className="text-ink-3 text-[11px] uppercase tracking-wide">
              <Trans>Saldo</Trans>
            </span>
            <span className="font-medium text-ink-1 tabular-nums">
              {formatMinor(bal.balance_cents, "usd")}
            </span>
          </span>
          <span className="flex flex-col">
            <span className="text-ink-3 text-[11px] uppercase tracking-wide">
              <Trans>Assegnato</Trans>
            </span>
            <span className="text-ink-2 tabular-nums">{formatMinor(bal.granted_cents, "usd")}</span>
          </span>
          <span className="flex flex-col">
            <span className="text-ink-3 text-[11px] uppercase tracking-wide">
              <Trans>Consumato</Trans>
            </span>
            <span className="text-ink-2 tabular-nums">
              {formatMinor(bal.consumed_cents, "usd")}
            </span>
          </span>
        </div>
      ) : null}

      <div className="flex items-end gap-2">
        <label className="flex flex-col gap-1.5">
          <span className="font-medium text-[11px] text-ink-3 uppercase tracking-wide">
            <Trans>Assegna credito (USD)</Trans>
          </span>
          <input
            type="number"
            inputMode="decimal"
            min={0}
            step={1}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            aria-label={i18n._(msg`Importo credito in USD`)}
            className="h-9 w-40 rounded-md border border-hairline bg-card px-2.5 text-right text-[13px] text-ink-1 tabular-nums focus-visible:border-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
          />
        </label>
        <Button variant="default" size="sm" onClick={() => grant.mutate()} disabled={!canGrant}>
          {grant.isPending ? (
            <Loader2 size={14} className="mr-1 animate-spin" aria-hidden />
          ) : (
            <Wallet size={14} className="mr-1" aria-hidden />
          )}
          <Trans>Assegna</Trans>
        </Button>
      </div>
    </section>
  );
}

export function OrgBillingTab({ orgId }: OrgBillingTabProps): React.ReactElement {
  const { i18n } = useLingui();
  const orgQuery = useAdminOrg(orgId);
  const usageQuery = useAdminOrgUsage(orgId);
  const invoicesQuery = useAdminOrgInvoices(orgId);
  const { page, setPage, pageSize, setPageSize } = usePagination(25);
  const { toast } = useToast();

  // ``has_subscription`` isn't returned by the BE usage endpoint today,
  // so we mirror v1's heuristic: look at ``metadata.stripe_subscription_id``
  // on the ``AdminOrganization`` record. Treat any truthy string as
  // "active subscription" — matches v1 ``!!org.metadata?.stripe_subscription_id``.
  const hasSubscription = React.useMemo(() => {
    const meta = orgQuery.data?.metadata as Record<string, unknown> | undefined;
    const subId = meta?.stripe_subscription_id;
    return typeof subId === "string" && subId.length > 0;
  }, [orgQuery.data?.metadata]);

  const portalMutation = useMutation({
    mutationFn: () => createAdminOrgPortal(orgId),
    onSuccess: (data) => {
      if (data.portal_url) {
        window.open(data.portal_url, "_blank", "noopener,noreferrer");
      }
    },
    onError: () => {
      toast.error(i18n._(msg`Billing`), {
        description: i18n._(msg`Impossibile aprire il portale Stripe.`),
      });
    },
  });

  const checkoutMutation = useMutation({
    mutationFn: () => createAdminOrgCheckout(orgId),
    onSuccess: (data) => {
      if (data.checkout_url) {
        window.open(data.checkout_url, "_blank", "noopener,noreferrer");
      }
    },
    onError: () => {
      toast.error(i18n._(msg`Billing`), {
        description: i18n._(msg`Impossibile avviare il checkout Stripe.`),
      });
    },
  });

  const isRefreshing = usageQuery.isFetching || invoicesQuery.isFetching;

  const handleRefresh = React.useCallback(() => {
    void usageQuery.refetch();
    void invoicesQuery.refetch();
  }, [usageQuery, invoicesQuery]);

  const billingActionLoading = portalMutation.isPending || checkoutMutation.isPending;

  if (usageQuery.isLoading) {
    return <LoadingRow label={i18n._(msg`Caricamento utilizzo…`)} />;
  }

  const usage = usageQuery.data;
  const sortedMembers = usage
    ? [...usage.members].sort((a, b) => b.estimated_cost_eur - a.estimated_cost_eur)
    : [];
  const invoices = invoicesQuery.data ?? [];
  const pagedInvoices = invoices.slice((page - 1) * pageSize, page * pageSize);

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center justify-end gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={handleRefresh}
          disabled={isRefreshing}
          aria-label={i18n._(msg`Aggiorna utilizzo e fatture`)}
        >
          {isRefreshing ? (
            <Loader2 size={14} className="mr-2 animate-spin" aria-hidden />
          ) : (
            <RefreshCw size={14} className="mr-2" aria-hidden />
          )}
          {i18n._(msg`Aggiorna`)}
        </Button>
        {hasSubscription ? (
          <Button
            variant="outline"
            size="sm"
            onClick={() => portalMutation.mutate()}
            disabled={billingActionLoading}
            aria-label={i18n._(msg`Apri portale Stripe in una nuova scheda`)}
          >
            {portalMutation.isPending ? (
              <Loader2 size={14} className="mr-2 animate-spin" aria-hidden />
            ) : (
              <CreditCard size={14} className="mr-2" aria-hidden />
            )}
            {i18n._(msg`Gestisci abbonamento`)}
            <ExternalLink size={12} className="ml-1" aria-hidden />
          </Button>
        ) : (
          <Button
            size="sm"
            onClick={() => checkoutMutation.mutate()}
            disabled={billingActionLoading}
            aria-label={i18n._(msg`Avvia checkout Stripe in una nuova scheda`)}
          >
            {checkoutMutation.isPending ? (
              <Loader2 size={14} className="mr-2 animate-spin" aria-hidden />
            ) : (
              <CreditCard size={14} className="mr-2" aria-hidden />
            )}
            {i18n._(msg`Attiva abbonamento`)}
            <ExternalLink size={12} className="ml-1" aria-hidden />
          </Button>
        )}
      </div>

      <section aria-labelledby="org-billing-model-markup" className="flex flex-col gap-2">
        <h3 id="org-billing-model-markup" className="font-medium text-[13px] text-ink-1">
          <Trans>Commissione modelli Console</Trans>
        </h3>
        <p className="text-[11.5px] text-ink-3">
          <Trans>Questa organizzazione eredita la tariffa globale finché non salvi una percentuale specifica.</Trans>
        </p>
        <ModelMarkupCard tenantId={orgId} />
      </section>

      <section aria-labelledby="org-billing-markup" className="flex flex-col gap-2">
        <h3 id="org-billing-markup" className="font-medium text-[13px] text-ink-1">
          <Trans>Markup Engine legacy</Trans>
        </h3>
        <p className="text-[11.5px] text-ink-3">
          <Trans>Tariffa dei consumi Engine precedenti. Separata dalla commissione dei modelli Console.</Trans>
        </p>
        <GlobalMarkupCard tenantId={orgId} />
      </section>

      {/* Sits next to the markup: together they answer "what does this org pay
          and how" — the markup is the rate, this is the model and the timing. */}
      <OrgBillingConfigCard orgId={orgId} />

      <CreditsSection orgId={orgId} />

      <PlatformInvoicesSection orgId={orgId} />

      {usage ? (
        <section
          aria-labelledby="org-billing-usage"
          className="grid grid-cols-1 gap-3 md:grid-cols-2"
        >
          <div className="rounded-lg border border-hairline bg-card/55 p-4">
            <div className="mb-2 flex items-center gap-2">
              <BarChart3 size={14} aria-hidden className="text-accent" />
              <h3
                id="org-billing-usage"
                className="font-semibold text-[10px] text-ink-3 uppercase tracking-wide"
              >
                {i18n._(msg`Costo`)}
              </h3>
            </div>
            <div className="font-display font-semibold text-[20px] text-ink-1 tabular-nums">
              {formatEur(usage.cost_used_eur)}{" "}
              <span className="font-normal text-[12px] text-ink-3">
                {i18n._(msg`di ${formatEur(usage.cost_cap_eur)}`)}
              </span>
            </div>
            <div className="mt-2">
              <ProgressBar
                value={usage.cost_used_pct}
                ariaLabel={i18n._(msg`Percentuale costo utilizzato`)}
              />
              <div className="mt-1.5 flex justify-between text-[11px] text-ink-3 tabular-nums">
                <span>{i18n._(msg`${safeNum(usage.cost_used_pct).toFixed(1)}% usato`)}</span>
                <span>
                  {i18n._(
                    msg`${formatEur(
                      Math.max(0, usage.cost_cap_eur - usage.cost_used_eur),
                    )} disponibili`,
                  )}
                </span>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-hairline bg-card/55 p-4">
            <div className="mb-2 flex items-center gap-2">
              <HardDrive size={14} aria-hidden className="text-success" />
              <h3 className="font-semibold text-[10px] text-ink-3 uppercase tracking-wide">
                {i18n._(msg`Storage`)}
              </h3>
            </div>
            <div className="font-display font-semibold text-[20px] text-ink-1 tabular-nums">
              {formatBytes(usage.storage_used_bytes)}{" "}
              <span className="font-normal text-[12px] text-ink-3">
                {i18n._(msg`di ${formatBytes(usage.storage_limit_bytes)}`)}
              </span>
            </div>
            <div className="mt-2">
              <ProgressBar
                value={usage.storage_used_pct}
                ariaLabel={i18n._(msg`Percentuale storage utilizzato`)}
              />
              <div className="mt-1.5 flex justify-between text-[11px] text-ink-3 tabular-nums">
                <span>{i18n._(msg`${safeNum(usage.storage_used_pct).toFixed(1)}% usato`)}</span>
                <span>
                  {i18n._(
                    msg`${formatBytes(
                      Math.max(0, usage.storage_limit_bytes - usage.storage_used_bytes),
                    )} disponibili`,
                  )}
                </span>
              </div>
            </div>
          </div>
        </section>
      ) : null}

      {sortedMembers.length > 0 ? (
        <section
          aria-labelledby="org-billing-members"
          className="rounded-lg border border-hairline bg-card/55 p-5"
        >
          <div className="mb-3 flex items-center gap-2">
            <Users size={14} aria-hidden className="text-ink-3" />
            <h2
              id="org-billing-members"
              className="font-semibold text-[10px] text-ink-3 uppercase tracking-wide"
            >
              {i18n._(msg`Costi per membro`)}
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[12.5px]">
              <thead>
                <tr className="border-hairline border-b text-left">
                  <th className="px-3 py-2 font-semibold text-[10px] text-ink-3 uppercase tracking-wide">
                    {i18n._(msg`Nome`)}
                  </th>
                  <th className="hidden px-3 py-2 font-semibold text-[10px] text-ink-3 uppercase tracking-wide md:table-cell">
                    {i18n._(msg`Email`)}
                  </th>
                  <th className="px-3 py-2 text-right font-semibold text-[10px] text-ink-3 uppercase tracking-wide">
                    {i18n._(msg`Costo`)}
                  </th>
                  <th className="px-3 py-2 text-right font-semibold text-[10px] text-ink-3 uppercase tracking-wide">
                    {i18n._(msg`Storage`)}
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedMembers.map((m) => (
                  <tr key={m.user_id} className="border-hairline/60 border-b last:border-b-0">
                    <td className="h-8 px-3 py-2 align-middle font-medium text-ink-1">
                      {m.name ?? "—"}
                    </td>
                    <td className="hidden h-8 px-3 py-2 align-middle text-ink-3 md:table-cell">
                      {m.email}
                    </td>
                    <td className="h-8 px-3 py-2 text-right align-middle tabular-nums">
                      {formatEur(m.estimated_cost_eur)}
                    </td>
                    <td className="h-8 px-3 py-2 text-right align-middle tabular-nums">
                      {formatBytes(m.storage_bytes)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      <section
        aria-labelledby="org-billing-invoices"
        className="rounded-lg border border-hairline bg-card/55 p-5"
      >
        <div className="mb-3 flex items-center gap-2">
          <FileText size={14} aria-hidden className="text-ink-3" />
          <h2
            id="org-billing-invoices"
            className="font-semibold text-[10px] text-ink-3 uppercase tracking-wide"
          >
            {i18n._(msg`Fatture`)}
          </h2>
        </div>
        {invoicesQuery.isLoading ? (
          <LoadingRow label={i18n._(msg`Caricamento fatture…`)} />
        ) : invoices.length === 0 ? (
          <p className="py-6 text-center text-[12px] text-ink-3">
            {i18n._(msg`Nessuna fattura emessa per questa organizzazione.`)}
          </p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-[12.5px]">
                <thead>
                  <tr className="border-hairline border-b text-left">
                    <th className="px-3 py-2 font-semibold text-[10px] text-ink-3 uppercase tracking-wide">
                      {i18n._(msg`Data`)}
                    </th>
                    <th className="px-3 py-2 font-semibold text-[10px] text-ink-3 uppercase tracking-wide">
                      {i18n._(msg`Numero`)}
                    </th>
                    <th className="px-3 py-2 text-right font-semibold text-[10px] text-ink-3 uppercase tracking-wide">
                      {i18n._(msg`Importo`)}
                    </th>
                    <th className="px-3 py-2 font-semibold text-[10px] text-ink-3 uppercase tracking-wide">
                      {i18n._(msg`Stato`)}
                    </th>
                    <th className="px-3 py-2 text-right font-semibold text-[10px] text-ink-3 uppercase tracking-wide">
                      PDF
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {pagedInvoices.map((inv) => (
                    <tr key={inv.id} className="border-hairline/60 border-b last:border-b-0">
                      <td className="h-8 px-3 py-2 align-middle text-ink-3">
                        {new Date(inv.created).toLocaleDateString("it-IT")}
                      </td>
                      <td className="h-8 px-3 py-2 align-middle font-medium text-ink-1">
                        {inv.number ?? "—"}
                      </td>
                      <td className="h-8 px-3 py-2 text-right align-middle tabular-nums">
                        {formatEur(inv.amount_due)}
                      </td>
                      <td className="h-8 px-3 py-2 align-middle">
                        <Badge
                          variant={inv.status === "paid" ? "success" : "outline"}
                          className="text-[10px] uppercase"
                        >
                          {inv.status}
                        </Badge>
                      </td>
                      <td className="h-8 px-3 py-2 text-right align-middle">
                        {inv.invoice_pdf ? (
                          <a
                            href={inv.invoice_pdf}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label={i18n._(msg`Scarica PDF fattura ${inv.number ?? inv.id}`)}
                            className="inline-flex h-7 w-7 items-center justify-center rounded text-ink-3 transition-colors hover:bg-ando-orange-soft/40 hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                          >
                            <Download size={13} aria-hidden />
                          </a>
                        ) : (
                          <span className="text-ink-3">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <PaginationControls
              page={page}
              pageSize={pageSize}
              total={invoices.length}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
            />
          </>
        )}
      </section>
    </div>
  );
}
